import React, { useCallback } from 'react';

import {
  ArrayField,
  Datagrid,
  DateField,
  email,
  Labeled,
  TextField,
  usePermissions,
  useRecordContext,
  useRedirect,
} from 'react-admin';

import {
  Box,
  Button,
  Card,
  CardContent,
  CardHeader,
  Divider,
  Grid,
} from '@mui/material';

import hasAccess from '../../../utilities/hasAccess';

import countries from '../../../constants/countries';
import { boldDataGridStyle } from '../../../constants/style/datagridStyles';

import EditField from '../../../customFields/EditField';
import EditSelectInput from '../../../customFields/EditSelectInput';
import LatestRelatedCases from '../../../customFields/LatestRelatedCases';
import LatestRelatedPings from '../../../customFields/LatestRelatedPings';
import LatestRelatedRequests from '../../../customFields/LatestRelatedRequests';
import PerformKyc from '../../../customFields/PerformKyc';

import RiskManagement from '../../riskManagment';
import RelatedConductions from '../../../customFields/RelatedConductions';
import NationalIdentifier from '../../../customFields/NationalIdentifier';
import NoResults from '../../layout/NoResults';
import RelatedCustomConnections from '../../connection/RelatedCustomConnections';
import RelatedIpAddresses from '../../ipUsage/RelatedIpAddresses';
import DeviceUsages from '../../entities/DeviceUsages';
import actions, { Actions } from '../../../constants/actions';
import EditArrayInput from '../../../customFields/EditArrayInput';
import ReferenceEntityField from '../../../customFields/ReferenceEntityField';
import EntityDisplayNameField from '../../../customFields/EntityDisplayNameField';
import entityTypes from '../../../constants/entityTypes';
import PermissionMissing from '../../utils/PermissionMissing';

const Information = () => {
  const record = useRecordContext();
  const { permissions } = usePermissions<Actions[]>();
  const redirect = useRedirect();

  const handleKycTabChange = useCallback(() => {
    redirect('kyc-data/business-association', 'individuals', record?.id);
  }, [record?.id, redirect]);

  return (
    <Grid container spacing={6}>
      <Grid
        size={{
          xs: 12,
          lg: 5,
        }}
      >
        <Card variant="outlined">
          <CardHeader
            title="Individual details"
            action={
              hasAccess(permissions, actions.REQUEST_CREATE) && (<PerformKyc />)
            }
          />
          <Divider />
          <CardContent>
            <Grid container spacing={2}>
              <Grid size={12}>
                <Box>
                  <Labeled label="ID">
                    <TextField source="id" label="ID" />
                  </Labeled>
                </Box>
              </Grid>
              <Grid size={12}>
                <Box>
                  <Labeled label="Created at">
                    <DateField source="createdAt" />
                  </Labeled>
                </Box>
              </Grid>
              <Grid size={12}>
                <EditArrayInput showEdit={hasAccess(permissions, actions.INDIVIDUAL_UPDATE)} source="customIds" label="Custom IDs" />
              </Grid>
              <Grid size={12}>
                <EditField showEdit={hasAccess(permissions, actions.INDIVIDUAL_UPDATE)} source="firstName" label="Name" />
              </Grid>
              <Grid size={12}>
                <EditField showEdit={hasAccess(permissions, actions.INDIVIDUAL_UPDATE)} source="lastName" label="Last name" />
              </Grid>
              <Grid size={12}>
                <EditSelectInput choices={countries} showEdit={hasAccess(permissions, actions.INDIVIDUAL_UPDATE)} source="residence" label="Residence" />
              </Grid>
              <Grid size={12}>
                <EditField showEdit={hasAccess(permissions, actions.INDIVIDUAL_UPDATE)} source="email" label="Email" validate={[email()]} />
              </Grid>
              <Grid size={12}>
                <EditField showEdit={hasAccess(permissions, actions.INDIVIDUAL_UPDATE)} source="phoneNumber" label="Phone number" />
              </Grid>
              <Grid size={12}>
                <EditField showEdit={hasAccess(permissions, actions.INDIVIDUAL_UPDATE)} type="date" dateOptions={{ timeZone: 'UTC' }} source="birthDate" label="Birth date" />
              </Grid>
              <Grid size={12}>
                <EditField showEdit={hasAccess(permissions, actions.INDIVIDUAL_UPDATE)} type="date" source="kycApprovedAt" label="KYC approved" />
              </Grid>
              <Grid size={12}>
                <NationalIdentifier
                  showEdit={hasAccess(permissions, actions.INDIVIDUAL_UPDATE)}
                />
              </Grid>
              <Grid size={12}>
                <RiskManagement />
              </Grid>
            </Grid>
          </CardContent>
        </Card>
      </Grid>
      <Grid
        container
        direction="column"
        size={{
          xs: 12,
          lg: 7,
        }}
      >
        <Grid>
          <Card variant="outlined">
            <CardHeader
              title="Business associations"
              action={
                <Button onClick={handleKycTabChange} color="secondary">See more</Button>
              }
            />
            <Divider />
            <CardContent>
              { hasAccess(permissions, actions.BUSINESS_LIST) ? (
                <ArrayField label="Role(s)" source="relatedCompanies">
                  <Datagrid
                    sx={boldDataGridStyle}
                    bulkActionButtons={false}
                    empty={<NoResults />}
                  >
                    <ReferenceEntityField
                      source=""
                      label="Business"
                      idKey="companyId"
                      entityType={entityTypes.BUSINESS}
                    >
                      <EntityDisplayNameField />
                    </ReferenceEntityField>
                    <TextField source="roles" sortable={false} />
                  </Datagrid>
                </ArrayField>
              ) : (
                <Box display="flex" alignItems="center" width="100%" flexDirection="column">
                  <PermissionMissing />
                </Box>
              )}
            </CardContent>
          </Card>
        </Grid>
        <Grid>
          <RelatedCustomConnections />
        </Grid>
        <Grid>
          <RelatedIpAddresses />
        </Grid>
        <Grid>
          <DeviceUsages />
        </Grid>
      </Grid>
      <RelatedConductions />
      <LatestRelatedRequests />
      <LatestRelatedCases />
      <LatestRelatedPings />
    </Grid>
  );
};

export default Information;
